import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import apiClient from '@/lib/axios'

export type OrderStatus =
  | 'draft'
  | 'submitted'
  | 'preparing'
  | 'ready'
  | 'completed'
  | 'cancelled'

export interface OrderItem {
  id: string
  orderId: string
  menuItemId: string
  menuItemName?: string
  menuItemNameAr?: string
  quantity: number
  unitPrice: number
  price: number // Total for this line item (unitPrice * quantity)
  specialInstructions?: string | null
  createdAt: string
}

export interface Order {
  id: string
  branchId: string
  sessionId: string | null
  tableNumber?: string | null
  status: OrderStatus
  total: number // In piasters
  submittedAt?: string | null
  createdAt: string
  updatedAt: string
  items?: OrderItem[]
  itemCount?: number
}

export interface GetOrdersQueryParams {
  status?: OrderStatus
  search?: string
  dateFrom?: string // ISO-8601 date
  dateTo?: string // ISO-8601 date
  sortBy?: 'createdAt' | 'status' | 'total'
  sortOrder?: 'ASC' | 'DESC'
  page?: number
  pageSize?: number
}

export interface OrdersResponse {
  data: Order[]
  meta: {
    page: number
    pageSize: number
    total: number
    totalPages: number
    hasMore: boolean
  }
}

export function useOrders(params: GetOrdersQueryParams = {}) {
  const { getToken } = useAuth()

  const {
    page = 1,
    pageSize = 20,
    sortBy = 'createdAt',
    sortOrder = 'DESC',
    ...filters
  } = params

  return useQuery<OrdersResponse>({
    queryKey: ['orders', { page, pageSize, sortBy, sortOrder, ...filters }],
    queryFn: async () => {
      const token = await getToken()
      const response = await apiClient.get<OrdersResponse>('/orders', {
        params: {
          page,
          pageSize,
          sortBy,
          sortOrder,
          ...filters,
        },
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      })
      return response.data
    },
    staleTime: 10000, // 10 seconds - for semi-real-time updates
    gcTime: 300000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchInterval: 30000, // Auto-refresh every 30 seconds
    retry: 2,
  })
}

export function useOrder(orderId: string) {
  const { getToken } = useAuth()

  return useQuery<Order>({
    queryKey: ['orders', orderId],
    queryFn: async () => {
      const token = await getToken()
      const response = await apiClient.get<Order>(`/orders/${orderId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      })
      return response.data
    },
    enabled: !!orderId,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
    retry: 2,
  })
}

